import { useEffect, useId, useMemo, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import { cn } from '@/utils/cn';
import type { SelectOption } from '@/components/ui/Select';
import { FieldLabel, parseFieldLabel } from '@/components/ui/FieldLabel';
import { useDebounce } from '@/hooks/useDebounce';

export interface SearchableMultiSelectProps {
  label?: string;
  error?: string;
  hint?: string;
  options: SelectOption[];
  values?: string[];
  onChange?: (values: string[]) => void;
  disabled?: boolean;
  loading?: boolean;
  searching?: boolean;
  className?: string;
  id?: string;
  name?: string;
  required?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  filterLocally?: boolean;
  onSearchQuery?: (query: string) => void;
  searchDebounceMs?: number;
  /** Resolve chip label when option is not in the current list */
  getOptionLabel?: (value: string) => string | undefined;
}

export function SearchableMultiSelect({
  label,
  error,
  hint,
  options,
  values = [],
  onChange,
  disabled,
  loading,
  searching = false,
  className,
  id,
  name,
  required,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyMessage = 'No matches',
  filterLocally = true,
  onSearchQuery,
  searchDebounceMs = 300,
  getOptionLabel,
}: SearchableMultiSelectProps) {
  const autoId = useId();
  const fieldId = id ?? name ?? autoId;
  const { required: isRequired } = parseFieldLabel(label, required);
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, searchDebounceMs);
  const serverSearch = Boolean(onSearchQuery);

  const labelByValue = useMemo(() => {
    const map = new Map<string, string>();
    for (const opt of options) map.set(opt.value, opt.label);
    return map;
  }, [options]);

  useEffect(() => {
    if (!serverSearch) return;
    onSearchQuery?.(debouncedQuery);
  }, [debouncedQuery, onSearchQuery, serverSearch]);

  const filtered = useMemo(() => {
    if (!filterLocally || serverSearch) return options;
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (opt) => opt.label.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q),
    );
  }, [options, query, filterLocally, serverSearch]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const toggle = (next: string) => {
    if (values.includes(next)) {
      onChange?.(values.filter((v) => v !== next));
    } else {
      onChange?.([...values, next]);
    }
  };

  const remove = (next: string) => {
    onChange?.(values.filter((v) => v !== next));
  };

  const resolveLabel = (value: string) =>
    labelByValue.get(value) ?? getOptionLabel?.(value) ?? `#${value}`;

  const summary =
    values.length === 0
      ? placeholder
      : values.length === 1
        ? resolveLabel(values[0])
        : `${values.length} selected`;

  return (
    <div className={cn('relative min-w-0 w-full', className)} ref={rootRef}>
      <FieldLabel htmlFor={fieldId} label={label} required={required} />
      {hint && !error && <p className="mb-1.5 text-xs text-slate-500 dark:text-slate-400">{hint}</p>}
      <button
        type="button"
        id={fieldId}
        disabled={disabled || loading}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && !loading && setOpen((o) => !o)}
        className={cn(
          'input flex w-full min-w-0 items-center justify-between gap-2 text-left',
          values.length === 0 && 'text-slate-500 dark:text-slate-400',
          values.length > 0 && 'text-slate-900 dark:text-white',
          error && 'input-error',
          (disabled || loading) && 'cursor-not-allowed opacity-60',
        )}
      >
        <span className="truncate">{loading ? 'Loading…' : summary}</span>
        <KeyboardArrowDownIcon
          className={cn('shrink-0 text-slate-500 transition-transform dark:text-slate-400', open && 'rotate-180')}
          fontSize="small"
        />
      </button>
      {name && values.map((v) => (
        <input key={v} type="hidden" name={name} value={v} readOnly />
      ))}
      {values.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {values.map((v) => (
            <span
              key={v}
              className="inline-flex max-w-full items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200"
            >
              <span className="truncate">{resolveLabel(v)}</span>
              {!disabled && (
                <button
                  type="button"
                  className="rounded-full p-0.5 hover:bg-slate-200 dark:hover:bg-slate-700"
                  aria-label={`Remove ${resolveLabel(v)}`}
                  onClick={() => remove(v)}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-full min-w-[14rem] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-600 dark:bg-slate-800">
          <div className="border-b border-slate-100 p-2 dark:border-slate-700">
            <div className="relative">
              <SearchIcon
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
                sx={{ fontSize: 18 }}
              />
              <input
                ref={searchRef}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="input w-full py-1.5 pl-8 pr-2 text-sm"
                aria-label={searchPlaceholder}
              />
            </div>
          </div>
          <ul role="listbox" className="max-h-56 overflow-y-auto py-1" aria-multiselectable>
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">
                {searching ? 'Searching…' : emptyMessage}
              </li>
            )}
            {filtered.map((opt) => {
              const checked = values.includes(opt.value);
              return (
                <li key={opt.value} role="option" aria-selected={checked}>
                  <button
                    type="button"
                    disabled={opt.disabled}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-100 dark:hover:bg-slate-700',
                      checked && 'bg-primary-50 font-medium text-primary-700 dark:bg-primary-950/50 dark:text-primary-300',
                    )}
                    onClick={() => !opt.disabled && toggle(opt.value)}
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[10px]',
                        checked
                          ? 'border-primary-600 bg-primary-600 text-white'
                          : 'border-slate-300 dark:border-slate-600',
                      )}
                    >
                      {checked ? '✓' : ''}
                    </span>
                    <span className="truncate">{opt.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      {isRequired && values.length === 0 && !error && (
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Select at least one option</p>
      )}
    </div>
  );
}
